import {Component, inject, Injector, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, TemplateRef, ViewChild} from '@angular/core';
import {AlertTicket, EscalatedTicket, SiteService, Ticket, TicketType} from "@shared/services";
import {AceTableComponent, ColumnMode, PageInfo, TableColumn} from "@shared/components/ace-table/ace-table.component";
import {Observable, Subject, takeUntil} from "rxjs";
import {TicketPagedModel} from "@shared/services/api/tickets/tickets-api-client";
import {BrowserUtils} from "@shared/util/general/browser-utils";
import {finalize} from "rxjs/operators";
import {TicketDataCaches} from "@shared/data-stores/ticket-data-caches";
import {TicketUtils} from "@app/layout/sites/tickets/ticket-utils";
import {NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {AlertTicketDetailsModalComponent} from "@app/layout/sites/tickets/alert-ticket-details-modal/alert-ticket-details-modal.component";
import {EscalatedTicketDetailsModalComponent} from "@app/layout/sites/tickets/escalated-ticket-details-modal/escalated-ticket-details-modal.component";


type TicketProp = (keyof AlertTicket) | (keyof EscalatedTicket);


@Component({
  selector: 'app-tickets-table',
  templateUrl: './tickets-table.component.html',
  styleUrl: './tickets-table.component.scss',
  providers: [{
    provide: TicketDataCaches,
    useFactory: () => inject(TicketDataCaches, {optional: true, skipSelf: true})
      ?? Injector.create({providers: [TicketDataCaches], parent: inject(Injector)}).get(TicketDataCaches)
  }],
  standalone: false
})
export class TicketsTableComponent implements OnInit, OnChanges, OnDestroy {
  private readonly ngUnsubscribe = new Subject<void>();
  private readonly fallbackPageSize = 25;
  private readonly ticketFieldNamesByProp = new Map<TicketProp, string>([
    ['summary', 'Summary'],
    ['rule_id', 'Rule ID']
  ]);

  @Input() pageSize: number = this.fallbackPageSize;
  @Input() rows: Ticket[];
  @Input() count: number;
  @Input() columns: TableColumn[];
  @Input() loading = false;
  @Input() allowToOpenTickets = true;
  @Input() readOnly = false;
  @Input() ticketPageFn: (pageInfo: PageInfo) => Observable<TicketPagedModel<Ticket[]>>;

  @ViewChild('headerTemplate', {static: true}) headerTemplate: TemplateRef<any>;
  @ViewChild('cellTemplate', {static: true}) cellTemplate: TemplateRef<any>;

  protected readonly ColumnMode = ColumnMode;
  protected tableColumns: TableColumn[];

  private ticketModalInjector: Injector;

  constructor(private ticketDataCaches: TicketDataCaches,
              private injector: Injector,
              private modalService: NgbModal,
              private siteService: SiteService) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.columns) {
      this.updateTableColumns();
    }
  }

  ngOnInit(): void {
    if (!this.rows) {
      this.setPage({offset: 0, limit: this.pageSize});
    }
    if (!this.tableColumns) {
      this.updateTableColumns();
    }
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  protected ticketRowSelected(activateEvent: any): void {
    if (activateEvent.type === 'click' && !BrowserUtils.isTextSelected() && this.allowToOpenTickets) {
      AceTableComponent.fixExpressionChangedBug(activateEvent.event);
      const ticket: Ticket = activateEvent.row;
      this.siteService.getCachedSiteInfo(ticket.site_id).subscribe(site => {
        const modalOptions = {injector: this.getTicketModalInjector()};
        const modalData = {ticket, site, readOnly: this.readOnly};
        if (ticket.type === TicketType.ALERT) {
          AlertTicketDetailsModalComponent.openInModal(this.modalService, modalData, modalOptions);
        } else if (ticket.type === TicketType.ESCALATED) {
          EscalatedTicketDetailsModalComponent.openInModal(this.modalService, modalData, modalOptions);
        } else {
          throw new Error('Unsupported ticket type ' + ticket.type);
        }
      });
    }
  }

  protected setPage(pageInfo: PageInfo): void {
    this.loading = true;
    this.ticketPageFn(pageInfo).pipe(finalize(() => this.loading = false), takeUntil(this.ngUnsubscribe)).subscribe(ticketPage => {
      this.count = ticketPage.count;
      this.rows = ticketPage.data;
    });
  }

  private updateTableColumns() {
    if (this.columns?.length) {
      const defaultTableColumn: TableColumn = {
        headerTemplate: this.headerTemplate,
        cellTemplate: this.cellTemplate,
        flexGrow: 1
      };
      this.tableColumns = this.columns.map(column => ({
        ...defaultTableColumn,
        ...column.name == null && {name: this.ticketFieldNamesByProp.get(<TicketProp>column.prop)},
        ...column
      }));
    } else {
      this.tableColumns = null;
    }
  }

  private getTicketModalInjector(): Injector {
    if (!this.ticketModalInjector) {
      this.ticketModalInjector = TicketUtils.makeTicketDataCachesInjector(this.ticketDataCaches, this.injector);
    }
    return this.ticketModalInjector;
  }
}
